import { useEffect, useState } from 'react';
import { Check, ClipboardList, FilePenLine, Link2 } from 'lucide-react';
import { api } from '../lib/api';
import { NoCargo } from './NoCargo';
import { useFallo } from '../lib/useFallo';
import { useI18n } from '../lib/i18n';
import { copiarAlPortapapeles } from '../lib/portapapeles';
import { Module } from './ui/Module';
import { RChip } from './ui/RChip';

export type IntakeQuestion = {
  key: string; label: string; type: string; options?: string[];
  showIf?: { key: string; equals: string };
};
export type IntakeSubmission = {
  id: string; public_token: string; submitted_at: string;
  full_name: string | null; answers: Record<string, unknown>; questions: IntakeQuestion[];
};

const shouldShow = (q: IntakeQuestion, answers: Record<string, unknown>) =>
  !q.showIf || String(answers[q.showIf.key] ?? '') === q.showIf.equals;

/** Lista Q&A del intake (label → respuesta), respetando las condicionales (showIf). */
export function IntakeAnswersList({ questions, answers }: { questions: IntakeQuestion[]; answers: Record<string, unknown> }) {
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {questions.filter((q) => shouldShow(q, answers)).map((q) => {
        const v = answers[q.key];
        const val = v == null || v === '' ? '—' : String(v);
        return (
          <div key={q.key} style={{ borderBottom: '1px solid var(--line)', paddingBottom: 6 }}>
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{q.label}</div>
            <div style={{ fontSize: 'var(--fs-sm)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontWeight: 500, color: val === '—' ? 'var(--muted)' : 'var(--ink)' }}>{val}</div>
          </div>
        );
      })}
    </div>
  );
}

type Edit = { question_key: string; valor_anterior: string | null; valor_nuevo: string | null; quien: string | null; cuando: string };
type Questionnaire = {
  questions: IntakeQuestion[];
  answers: Record<string, unknown>;
  submission: { public_token: string; submitted_at: string } | null;
  /** Lo que escribió el HOMEOWNER en el form, sin el merge del staff encima. */
  original?: Record<string, unknown>;
  /** Quién cambió qué y cuándo (lo más reciente primero). */
  edits?: Edit[];
};

/**
 * Módulo "Homeowner Questions": SIEMPRE muestra las preguntas del intake (aunque estén
 * vacías) para irlas llenando por teléfono en Welcome Call / Claim Review. Editable por
 * cualquier usuario office+ (se guarda en el claim, mergeado sobre lo que el homeowner
 * haya llenado en el form). Si el homeowner llenó el form público, muestra además el link
 * para ver/enviar el formulario lleno.
 */
export function IntakeSubmissionBox({ claimId, defaultOpen = false }: { claimId: string; defaultOpen?: boolean }) {
  const { t } = useI18n();
  const [data, setData] = useState<Questionnaire | null>(null);
  const [vals, setVals] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const { fallo, marcar, limpiar } = useFallo();
  const load = () => api.get<Questionnaire>(`/api/claims/${claimId}/questionnaire`).then((r) => {
    setData(r);
    setVals(Object.fromEntries((r.questions || []).map((q) => [q.key, r.answers[q.key] == null ? '' : String(r.answers[q.key])])));
  }).then(limpiar).catch(marcar);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [claimId]);
  // Antes un fallo dejaba data en null y esto hacía DESAPARECER la caja del cuestionario.
  if (fallo) return <NoCargo onRetry={load} compact />;
  if (!data) return null;

  const set = (k: string, v: string) => setVals((p) => ({ ...p, [k]: v }));
  const answered = (data.questions || []).filter((q) => shouldShow(q, vals) && String(vals[q.key] ?? '').trim() !== '').length;
  const shown = (data.questions || []).filter((q) => shouldShow(q, vals));

  async function save() {
    setBusy(true);
    try { await api.put(`/api/claims/${claimId}/questionnaire`, { answers: vals }); setSaved(true); setTimeout(() => setSaved(false), 2000); await load(); }
    catch { alert('Error'); } finally { setBusy(false); }
  }

  const sub = data.submission;
  const url = sub ? `${window.location.origin}/intake/view/${sub.public_token}` : '';
  const fmt = (iso: string) => new Date(iso).toLocaleString('en-US');
  // Acá el rechazo ya estaba atajado, pero sin camino alternativo: el clic no copiaba nada. Por el
  // helper, que prueba el método viejo antes de darse por vencido; el ✓ solo si copió.
  async function copy() { if (url && await copiarAlPortapapeles(url)) { setCopied(true); setTimeout(() => setCopied(false), 2000); } }

  // Módulo estándar del rediseño: chip con avance y estado del form; preview al cerrar.
  const chip = (
    <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)' }}>{answered}/{shown.length}</span>
      {sub
        ? <RChip variant="ok" icon={Check}>{t('intk.submittedOn')} {fmt(sub.submitted_at)}</RChip>
        : <RChip variant="warn">{t('intk.missing')}</RChip>}
    </span>
  );
  return (
    <Module id={`intake-questions:${claimId}`} icon={FilePenLine} title={t('intk.questionsTitle')} chip={chip}
      preview={`${answered}/${shown.length} · ${sub ? `${t('intk.submittedOn')} ${fmt(sub.submitted_at)}` : t('intk.missing')}`}
      defaultOpen={defaultOpen}>
      <div style={{ display: 'grid', gap: 8, paddingTop: 2 }}>
        {sub && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <a href={url} target="_blank" rel="noopener noreferrer" className="btn ghost sm"><Link2 size={14} strokeWidth={1.75} /> {t('intk.viewFilled')}</a>
            <button className="btn ghost sm" onClick={() => { void copy(); }}>{copied ? <><Check size={14} strokeWidth={1.75} /> {t('intk.copied')}</> : <><ClipboardList size={14} strokeWidth={1.75} /> {t('intk.copyLink')}</>}</button>
          </div>
        )}
        {/* 2 columnas (responsive) + alto fijo con scroll → no ocupa tanto vertical. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 8, maxHeight: 260, overflowY: 'auto', paddingRight: 4 }}>
        {shown.map((q) => {
          /* ⚠️ LO QUE HABÍA PUESTO EL CLIENTE, CUANDO NO ES LO QUE SE VE.
             El cuestionario MEZCLA el formulario público con lo que el staff completa en la
             llamada, y el staff gana. Eso es correcto —corregir por teléfono es el trabajo— pero
             la pantalla no lo decía: se veía una respuesta distinta a la que el cliente escribió,
             sin marca, sin autor y sin forma de recuperar la original. Fue exactamente la pregunta
             del dueño sobre Gary Graham. Ahora cada respuesta corregida lo dice. */
          const orig = data?.original?.[q.key];
          const actual = String(vals[q.key] ?? '');
          const cambiada = orig != null && String(orig) !== '' && String(orig) !== actual;
          const edit = data?.edits?.find((e) => e.question_key === q.key); // el más reciente
          return (
          <label key={q.key} style={{ display: 'grid', gap: 3, alignContent: 'start' }}>
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{q.label}</span>
            {q.options && q.options.length > 0 ? (
              <select className="input sm" value={vals[q.key] ?? ''} onChange={(e) => set(q.key, e.target.value)}>
                <option value="">—</option>
                {q.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : q.type === 'textarea' || q.type === 'notes' ? (
              <textarea className="input sm" style={{ minHeight: 48 }} value={vals[q.key] ?? ''} onChange={(e) => set(q.key, e.target.value)} />
            ) : (
              <input className="input sm" type={q.type === 'date' ? 'date' : q.type === 'tel' ? 'tel' : q.type === 'email' ? 'email' : 'text'}
                value={vals[q.key] ?? ''} onChange={(e) => set(q.key, e.target.value)} />
            )}
            {/* La respuesta ORIGINAL del cliente, cuando lo que se ve ya no es lo que él escribió.
                Con autor y fecha si sabemos quién la cambió — igual que los links de fotos, que
                guardan quién los agregó. */}
            {cambiada && (
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)', lineHeight: 1.35 }}>
                <span style={{ color: 'var(--orange)', fontWeight: 700 }}>✎ {t('iq.wasEdited')}</span>{' '}
                {t('iq.clientSaid')}: <span style={{ textDecoration: 'line-through' }}>{String(orig)}</span>
                {edit?.quien && <> · {t('iq.changedBy', { quien: edit.quien, cuando: new Date(edit.cuando).toLocaleString('en-US') })}</>}
              </span>
            )}
          </label>
          );
        })}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn sm" disabled={busy} onClick={save}>{busy ? '…' : t('common.save')}</button>
          {saved && <span className="muted" style={{ fontSize: 'var(--fs-2xs)', color: 'var(--green)' }}><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('common.saved')}</span>}
        </div>
      </div>
    </Module>
  );
}
